import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import type { UnitType } from "@repo/supabase/database-types"
import { getIsomorphicSupabaseClient } from "@repo/supabase/isomorphic-client"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import { createUnitsService } from "./units.service"

/**
 * Query key factory for units operations
 * Follows the pattern: [module, operation, ...params]
 */
export const unitsQueryKeys = {
  units: () => ["units"] as const,

  unitsBySlug: (unitType: UnitType, unitSlug: string) =>
    [...unitsQueryKeys.units(), unitType, unitSlug] as const,
  unitMembers: (unitId: string, filters?: DataTableBaseSearch) =>
    [...unitsQueryKeys.units(), "members", unitId, filters] as const,
} as const

/**
 * Get the internal unit (singleton)
 */
export const getUnitBySlugQueryOptions = (
  unitType: UnitType,
  unitSlug: string,
) =>
  queryOptions({
    queryKey: unitsQueryKeys.unitsBySlug(unitType, unitSlug),
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const unitsService = createUnitsService(supabase)

      return unitsService.getUnitBySlug({
        slug: unitSlug,
        unitType,
      })
    },
    staleTime: 1000 * 60 * 10, // 10 minutes (unit rarely changes)
    placeholderData: keepPreviousData,
    enabled: Boolean(unitType && unitSlug),
  })

/**
 * Get unit members with pagination and filtering
 */
export const getUnitMembersQueryOptions = (
  unitId: string,
  filters?: DataTableBaseSearch,
) =>
  queryOptions({
    queryKey: unitsQueryKeys.unitMembers(unitId, filters),
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const unitsService = createUnitsService(supabase)

      const limit = Math.max(1, filters?.page_size ?? 200)
      const offset = Math.max(0, (filters?.page ?? 1) - 1) * limit

      const members = await unitsService.getUnitMembers({
        unitId,
        limit,
        offset,
      })

      return {
        members,
        count: members.length,
      }
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: !!unitId,
  })
